import { ArrowLeftIcon } from '@heroicons/react/24/outline';

/**
 * The step-back control for the Yes/No follow-up chain (client request, 2026-07-28).
 *
 * The opening-bid flow is a one-way funnel — No → "Why not?" → the bid pad — and until this
 * button existed a mis-press was unrecoverable past the first step: Yes stayed live after "No",
 * but nothing led from the bid pad back to "Insufficient points". Each follow-up step now leads
 * with one of these, on EVERY viewport: on phones it is the only way back (the earlier step is
 * swapped out entirely — see `padswap` in tailwind.config.js), and on desktop, where the steps
 * stack, it is still the only way back from the bid pad.
 *
 * One shared component so the two steps cannot drift apart in look or behaviour. A bare arrow
 * glyph is enough (client decision): it sits beside the step's own prompt, which says where the
 * learner is; `label` says where the arrow goes, for the accessible name and the hover tooltip.
 */
export interface StepBackButtonProps {
  onClick: () => void;
  disabled?: boolean;
  /** Where the button leads, e.g. "Back to Yes or No" — the accessible name and tooltip. */
  label: string;
  testId: string;
}

export function StepBackButton({ onClick, disabled = false, label, testId }: StepBackButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testId}
      // The pads' neutral card recipe (border/bg-card/shadow), shrunk to an icon chip, so the
      // control reads as part of the pad it heads rather than as another answer button.
      //
      // A filled green version (YesNoPad's "Yes" recipe) was tried on 2026-07-28 to make it
      // stand out more and rejected on sight — it read as a third answer. Visibility on the
      // cream panel is still an open question with the client; whatever replaces this, it is
      // not simply "make it look like Yes".
      className="shrink-0 rounded-full border border-line-strong bg-card p-1.5 text-ink shadow-[0_1px_0_#d8ceae] transition-colors hover:bg-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 disabled:cursor-not-allowed disabled:opacity-60 padswap:p-1"
    >
      <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}
